import { useCallback } from 'react';
import {
  Chip,
  Link as MuiLink,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import { Link as RouterLink } from 'react-router-dom';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import type { DragEndEvent } from '@dnd-kit/core';
import {
  SortableContext,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

import { useUpdateRunningOrder } from '@/queries/useTcRun';
import type { TcRunDetailResponse, TcRunAppraiseeResponse } from '@/api/types/tcRun.types';
import {
  formatAppraisalStatusLabel as formatStatusLabel,
  getAppraisalStatusChipSx as getStatusChipSx,
} from '../appraisalStatusChip';

interface AppraiseeTableProps {
  tcRunDetail: TcRunDetailResponse;
  selectedAppraisalId: number | null;
  onSelectAppraisee: (appraisalId: number) => void;
  readOnly: boolean;
}

interface SortableRowProps {
  appraisee: TcRunAppraiseeResponse;
  index: number;
  isSelected: boolean;
  onSelect: (appraisalId: number) => void;
  readOnly: boolean;
}

function SortableRow({ appraisee, index, isSelected, onSelect, readOnly }: SortableRowProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: appraisee.appraisalId });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <TableRow
      ref={setNodeRef}
      style={style}
      hover
      selected={isSelected}
      onClick={() => onSelect(appraisee.appraisalId)}
      sx={{ cursor: 'pointer' }}
    >
      <TableCell>
        {!readOnly && (
          <DragIndicatorIcon
            {...attributes}
            {...listeners}
            sx={{ cursor: 'grab', verticalAlign: 'middle', mr: 1, color: 'text.secondary' }}
            fontSize="small"
          />
        )}
        {index + 1}
      </TableCell>
      <TableCell>
        <MuiLink
          component={RouterLink}
          to={`/appraisals/${appraisee.appraisalId}`}
          onClick={(e) => e.stopPropagation()}
          underline="hover"
        >
          {appraisee.firstName} {appraisee.lastName}
        </MuiLink>
      </TableCell>
      <TableCell>{appraisee.tenureInGrade ?? '-'}</TableCell>
      <TableCell>{appraisee.proposedRating ?? '-'}</TableCell>
      <TableCell>{appraisee.finalRating ?? '-'}</TableCell>
      <TableCell>{appraisee.geo ?? '-'}</TableCell>
      <TableCell>{appraisee.collatorName ?? '-'}</TableCell>
      <TableCell>
        <Chip
          label={formatStatusLabel(appraisee.status)}
          size="small"
          sx={{
            minWidth: 100,
            justifyContent: 'center',
            ...getStatusChipSx(appraisee.status),
          }}
        />
      </TableCell>
    </TableRow>
  );
}

export function AppraiseeTable({
  tcRunDetail,
  selectedAppraisalId,
  onSelectAppraisee,
  readOnly,
}: AppraiseeTableProps) {
  const updateRunningOrderMutation = useUpdateRunningOrder();

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor)
  );

  const appraisees = tcRunDetail.appraisees;
  const sortableIds = appraisees.map((a) => a.appraisalId);

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;
      if (!over || active.id === over.id) return;

      const oldIndex = appraisees.findIndex((a) => a.appraisalId === active.id);
      const newIndex = appraisees.findIndex((a) => a.appraisalId === over.id);
      if (oldIndex === -1 || newIndex === -1) return;

      // Build new order
      const reordered = [...appraisees];
      const [moved] = reordered.splice(oldIndex, 1);
      reordered.splice(newIndex, 0, moved);

      const items = reordered.map((a, idx) => ({
        appraisalId: a.appraisalId,
        newOrder: idx + 1,
      }));

      updateRunningOrderMutation.mutate({
        tcId: tcRunDetail.talentCommitteeId,
        items,
        rowVersion: tcRunDetail.rowVersion,
      });
    },
    [appraisees, tcRunDetail, updateRunningOrderMutation]
  );

  return (
    <TableContainer
      sx={{
        '& tbody .MuiTableCell-root': { py: 1.5, px: 2, borderBottom: 'none' },
        '& tbody tr:nth-of-type(odd)': { bgcolor: 'action.hover' },
        '& tbody tr.Mui-selected': { bgcolor: 'transparent' },
        '& tbody tr.Mui-selected:nth-of-type(odd)': { bgcolor: 'action.hover' },
        '& tbody tr.Mui-selected:hover, & tbody tr.Mui-selected:nth-of-type(odd):hover':
          { bgcolor: 'action.hover' },
      }}
    >
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
      >
        <SortableContext items={sortableIds} strategy={verticalListSortingStrategy}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Order</TableCell>
                <TableCell>Appraisee Name</TableCell>
                <TableCell>Tenure in Grade</TableCell>
                <TableCell>Proposed Rating</TableCell>
                <TableCell>Final Rating</TableCell>
                <TableCell>Geo</TableCell>
                <TableCell>Collator</TableCell>
                <TableCell>Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {appraisees.map((appraisee, index) => (
                <SortableRow
                  key={appraisee.appraisalId}
                  appraisee={appraisee}
                  index={index}
                  isSelected={appraisee.appraisalId === selectedAppraisalId}
                  onSelect={onSelectAppraisee}
                  readOnly={readOnly}
                />
              ))}
            </TableBody>
          </Table>
        </SortableContext>
      </DndContext>
    </TableContainer>
  );
}
